import { Partner } from './partner-types';
import Icon from './Icon';
import { useState } from 'preact/hooks';
import { getAddressString } from './helpers';
type Props = Partner & {
	layout: 'list' | 'map';
	focusCallback?: (node: HTMLElement | null) => void;
};

export default function PartnerListCard({
	id,
	title,
	url,
	type,
	contact,
	address,
	image,
	layout,
	focusCallback,
	moreInformation,
	branches = [],
}: Props) {
	const [expandedInfo, setExpandedInfo] = useState(false);
	const [expandedBranches, setExpandedBranches] = useState(false);

	const isNetwork = type === 'network';
	const idString = `partner-${id}`;
	const moreInfoId = `partner-more-info-${id}`;
	const addressString = getAddressString(address);

	const handleLogoClick = () => {
		if (!url || !image) return;

		window.open(url, '_blank');
	};

	const cssClasses = `partner-card partner-card--${layout}`;

	return (
		<article className={cssClasses} aria-labelledby={idString}>
			<div
				className={`partner-card__type ${
					isNetwork
						? 'partner-card__type--network'
						: 'partner-card__type--region'
				}`}
			>
				{isNetwork ? 'Netzwerkpartner' : 'Regionaler Partner'}
			</div>
			<div className="partner-card__content">
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
				<div
					onClick={handleLogoClick}
					className="partner-card__image"
					dangerouslySetInnerHTML={{ __html: image }}
				/>
				<div className="partner-card__address">
					<h3
						className="partner-card__title"
						id={idString}
						ref={focusCallback}
						tabIndex={-1}
					>
						{title}
					</h3>
					{url && (
						<a
							className="icon-wrapper icon-wrapper--start partner-card__url"
							href={url}
							target="_blank"
						>
							<Icon id="external-link" />
							{url}
						</a>
					)}
					{addressString && (
						<div className="partner-card__address-string icon-wrapper icon-wrapper--start">
							<Icon id="map-pin" height="1.3em" />
							<span>{addressString}</span>
						</div>
					)}
				</div>
				<div className="partner-card__contact">
					{contact.name && (
						<h3 className="partner-card__contact-name">{contact.name}</h3>
					)}
					{contact.email && (
						<a
							href={`mailto:${contact.email}`}
							className="partner-card__mail-link wp-block-button__link  has-primary-background-color has-background wp-element-button"
						>
							{contact.email}
						</a>
					)}
					{contact.phone && (
						<a
							href={`tel:${contact.phone}`}
							className="partner-card__phone-link wp-block-button__link  has-primary-background-color has-background wp-element-button"
						>
							{contact.phone}
						</a>
					)}
				</div>
				{branches && branches.length > 0 && (
					<div className="partner-card__more-information">
						<div className="wp-block-boom-accordion accordion">
							<div className="accordion-item">
								{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
								<h4
									className="accordion-header"
									onClick={() => setExpandedBranches(!expandedBranches)}
									aria-describedby={idString}
								>
									<button
										className={`accordion-button ${
											expandedBranches ? '' : 'collapsed'
										}`}
										aria-expanded={expandedBranches}
										aria-controls={moreInfoId}
									>
										<span className="accordion-button-text">
											Weitere Ansprechperson{branches.length > 1 ? 'en' : ''}
										</span>
									</button>
								</h4>
								<div
									className="partner-card__more-information__content"
									data-expanded={expandedBranches}
								>
									<div
										id={moreInfoId}
										className={`accordion-collapse collapse ${
											expandedBranches ? 'show' : ''
										}`}
									>
										<div className="accordion-body" aria-hidden={!expandedBranches}>
											{branches.map(
												({ contact, address, branchName }, index) => {
													const branchAddressString = getAddressString(address);
													const branchIdString = `${idString}-branch-${index}`;
													return (
														<section
															className="partner-card__branch"
															aria-labelledby={branchIdString}
															key={index}
														>
															<div>
																<h5
																	className="partner-card__title partner-card__branch-title"
																	id={branchIdString}
																>
																	{branchName}
																</h5>
																{branchAddressString && (
																	<div className="partner-card__address-string icon-wrapper icon-wrapper--start">
																		<Icon id="map-pin" height="1.3em" />
																		<span>{branchAddressString}</span>
																	</div>
																)}
															</div>
															<div>
																{contact.name && (
																	<h6 className="partner-card__contact-name">
																		{contact.name}
																	</h6>
																)}
																{contact.email && (
																	<a
																		href={`mailto:${contact.email}`}
																		className="partner-card__mail-link wp-block-button__link  has-primary-background-color has-background wp-element-button"
																	>
																		{contact.email}
																	</a>
																)}
																{contact.phone && (
																	<a
																		href={`tel:${contact.phone}`}
																		className="partner-card__phone-link wp-block-button__link  has-primary-background-color has-background wp-element-button"
																	>
																		{contact.phone}
																	</a>
																)}
															</div>
														</section>
													);
												},
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
				{moreInformation && moreInformation.content && (
					<div className="partner-card__more-information">
						<div className="wp-block-boom-accordion accordion">
							<div className="accordion-item">
								{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
								<h3
									className="accordion-header"
									onClick={() => setExpandedInfo(!expandedInfo)}
									aria-describedby={idString}
								>
									<button
										className={`accordion-button ${
											expandedInfo ? '' : 'collapsed'
										}`}
										aria-expanded={expandedInfo}
										aria-controls={moreInfoId}
									>
										<span className="accordion-button-text">
											{moreInformation.title}
										</span>
									</button>
								</h3>
								<div
									className="partner-card__more-information__content"
									data-expanded={expandedInfo}
								>
									<div
										id={moreInfoId}
										className={`accordion-collapse collapse ${
											expandedInfo ? 'show' : ''
										}`}
									>
										<div
											className="accordion-body"
											dangerouslySetInnerHTML={{
												__html: moreInformation.content,
											}}
											aria-hidden={!expandedInfo}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</article>
	);
}
